import React from 'react';
import { SuperShowcasePathData, SuperShowcasePathPoint, SuperShowcasePathValue } from '../../services/api';

type Props = {
  pathData: SuperShowcasePathValue;
  color: string;
  animated?: boolean;
  hovered?: boolean;
  selected?: boolean;
  interactive?: boolean;
  onClick?: React.MouseEventHandler<SVGSVGElement>;
  onPointerDown?: React.PointerEventHandler<SVGSVGElement>;
  onDoubleClick?: React.MouseEventHandler<SVGSVGElement>;
  onMouseEnter?: React.MouseEventHandler<SVGSVGElement>;
  onMouseLeave?: React.MouseEventHandler<SVGSVGElement>;
};

export function normalizePathRings(pathData: SuperShowcasePathValue | null | undefined): SuperShowcasePathPoint[][] {
  if (Array.isArray(pathData)) return pathData.length >= 3 ? [pathData] : [];
  if (pathData && Array.isArray((pathData as SuperShowcasePathData).rings)) {
    return (pathData as SuperShowcasePathData).rings.filter((ring) => Array.isArray(ring) && ring.length >= 3);
  }
  return [];
}

export function freehandLocalPoints(points: SuperShowcasePathPoint[]): string {
  return freehandLocalRings([points])[0]?.join(' ') ?? '';
}

function freehandLocalRings(rings: SuperShowcasePathPoint[][]): string[][] {
  const all = rings.flat();
  if (!all.length) return [];
  const xs = all.map((point) => point.x); const ys = all.map((point) => point.y);
  const minX = Math.min(...xs); const maxX = Math.max(...xs); const minY = Math.min(...ys); const maxY = Math.max(...ys);
  const width = Math.max(maxX - minX, 0.001); const height = Math.max(maxY - minY, 0.001);
  return rings.map((ring) => ring.map((point) => `${((point.x - minX) / width) * 100},${((point.y - minY) / height) * 100}`));
}

function pathFromRings(rings: string[][]): string {
  return rings.map((ring) => ring.length ? `M ${ring.join(' L ')} Z` : '').filter(Boolean).join(' ');
}

export default function FreehandPolygonOverlay({ pathData, color, animated = false, hovered = false, selected = false, interactive = false, onClick, onPointerDown, onDoubleClick, onMouseEnter, onMouseLeave }: Props) {
  const rings = normalizePathRings(pathData);
  if (!rings.length) return null;
  const localRings = freehandLocalRings(rings);
  const d = pathFromRings(localRings);
  const active = hovered || selected;
  const strokeWidth = active ? 2.8 : 1.8;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden={!interactive} onClick={onClick} onPointerDown={onPointerDown} onDoubleClick={onDoubleClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', filter: `drop-shadow(0 0 ${active ? 10 : 4}px ${color}${active ? 'CC' : '88'})` }}>
      <style>{`@keyframes showcaseFreehandComet { 0% { stroke-dashoffset: 1000; opacity: 0; } 4% { opacity: 1; } 21% { opacity: 1; } 25% { stroke-dashoffset: 0; opacity: 0; } 100% { stroke-dashoffset: 0; opacity: 0; } }`}</style>
      <path d={d} fill={active ? `${color}26` : 'transparent'} fillRule="evenodd" stroke={color} strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" pointerEvents={interactive ? 'fill' : 'none'} />
      {localRings.map((ring, ringIndex) => ring.length > 1 && [0, 1, 2, 3].map((quarter) => {
        const start = Math.floor((quarter * ring.length) / 4); const end = Math.floor(((quarter + 1) * ring.length) / 4);
        const segment = ring.slice(start, end + 1); if (quarter === 3) segment.push(ring[0]);
        return <polyline key={`segment-${ringIndex}-${quarter}`} points={segment.join(' ')} fill="none" stroke={color} strokeWidth={active ? 4.8 : 3.4} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" opacity={active ? 1 : 0.9} pointerEvents="none" />;
      }))}
      {animated && <React.Fragment key="comet">
        <path d={d} fill="none" stroke={color} strokeWidth={active ? 3.4 : 2.5} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" pathLength={1000} strokeDasharray="110 890" style={{ animation: 'showcaseFreehandComet 3s linear infinite', filter: `drop-shadow(0 0 9px ${color}) drop-shadow(0 0 3px #ffffff)` }} pointerEvents="none" />
        <path d={d} fill="none" stroke="#ffffff" strokeWidth={active ? 1.5 : 1.1} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" pathLength={1000} strokeDasharray="42 958" style={{ animation: 'showcaseFreehandComet 3s linear infinite', filter: `drop-shadow(0 0 4px ${color})` }} pointerEvents="none" />
      </React.Fragment>}
    </svg>
  );
}
